"use client"

import React, { useEffect, useState } from "react"
import Image from "next/image"
import { useRouter } from "next/navigation"
import { loadProducts } from "@/actions/productActions"
import { QuoteRequestRO, QuoteRO } from "@ub/quoting-client"
import { DownloadIcon, SearchIcon, ShoppingCartIcon } from "lucide-react"
import { useTranslations } from "next-intl"

import { formatDate, formatPrice } from "@/lib/helper"
import { AccountContext } from "@/lib/storefront/account/account-service"
import { DataPagination } from "@/components/ui/components/data-pagination"
import ImagePreviewGroup from "@/components/ui/components/image-preview-group"

interface AccountQuotesClientProps {
  accountContext: AccountContext
  allItems: Array<(QuoteRO | QuoteRequestRO) & { isQuoteRequest: boolean }>
}

type QuoteStatus = "EXPIRED" | "NEW" | "RESPONDED" | "REJECTED"

interface QuoteCardProps {
  quote: (QuoteRO | QuoteRequestRO) & { isQuoteRequest: boolean }
  isoLocale: string
  isQuoteRequest: boolean
}

function getQuoteStatus(quote: QuoteRO | QuoteRequestRO, isQuoteRequest: boolean): QuoteStatus {
  if (isQuoteRequest) {
    return "NEW"
  }

  const quoteRO = quote as QuoteRO
  if (quoteRO.rejected) {
    return "REJECTED"
  }

  if (quoteRO.validToDate && new Date(quoteRO.validToDate) < new Date()) {
    return "EXPIRED"
  }

  return "RESPONDED"
}

function getStatusColor(status: QuoteStatus): string {
  switch (status) {
    case "EXPIRED":
      return "text-gray-600"
    case "NEW":
      return "text-blue-600"
    case "RESPONDED":
      return "text-green-600"
    case "REJECTED":
      return "text-red-600"
    default:
      return "text-gray-600"
  }
}

function QuoteCard({ quote, isoLocale, isQuoteRequest }: QuoteCardProps) {
  const t = useTranslations("storefront")
  const status = getQuoteStatus(quote, isQuoteRequest)
  const statusColor = getStatusColor(status)
  const [products, setProducts] = useState<any[]>([])
  const router = useRouter()

  useEffect(() => {
    const productSkus = isQuoteRequest
      ? (quote as QuoteRequestRO).items?.map((item) => (item as any).productSKU) || []
      : (quote as QuoteRO).items?.map((item) => (item as any).productSKU) || []
    loadProducts(productSkus).then((loadedProducts) => setProducts(loadedProducts))
  }, [quote, isQuoteRequest])

  const handleNavigate = () => {
    router.push(`/account/quote/${quote.id}`)
  }

  return (
    <div
      className="relative flex flex-col cursor-pointer gap-4 rounded-lg border border-gray-300 p-6 transition-transform hover:shadow-lg hover:-translate-y-1"
      onClick={handleNavigate}
    >
      {/* Header */}
      <div className="flex justify-between items-start">
        <h2 className="text-lg font-bold text-gray-900">
          {t("quote.process_number")}: {quote.number}
        </h2>
        <button className="text-2xl text-gray-700">&times;</button>
      </div>

      {/* Main Content */}
      <div className="flex gap-4">
        {/* Left Images Section */}
        <div className="flex flex-col items-start gap-1">
          <ImagePreviewGroup imageUrls={products?.map((p) => p?.image)}></ImagePreviewGroup>
        </div>

        {/* Right Info Section */}
        <div className="flex-1 grid grid-cols-2 text-sm gap-x-2 gap-y-1">
          <div className="font-bold text-gray-800">{t("quote.reference")}</div>
          <div>{quote.displayName}</div>

          <div className="font-bold text-gray-800">{t("quote.creation_date")}</div>
          <div>{formatDate(isoLocale, quote.creationDate)}</div>

          <div className="font-bold text-gray-800">{t("quote.expiry_date")}</div>
          <div>{isQuoteRequest ? "-" : formatDate(isoLocale, (quote as QuoteRO).validToDate)}</div>

          <div className="font-bold text-gray-800">{t("quote.status_label")}</div>
          <div className={`${statusColor} font-medium`}>{t(`quote.status.${status}`)}</div>

          {!isQuoteRequest && (
            <>
              <div className="font-bold text-gray-800">{t("quote.total_net")}</div>
              <div className="font-bold text-lg">
                {formatPrice(isoLocale, (quote as QuoteRO)?.total?.value, (quote as QuoteRO)?.total?.currency)}
              </div>
            </>
          )}
        </div>

        {/* Right Arrow Section */}
        <div className="flex items-center">
          <div className="flex w-[30px]">
            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
              <line x1="0" y1="0" x2="100" y2="50" stroke="#f5f5f5" strokeWidth="10"></line>
              <line x1="0" y1="100" x2="100" y2="50" stroke="#f5f5f5" strokeWidth="10"></line>
            </svg>
          </div>
        </div>
      </div>

      {/* Divider */}
      <hr className="my-2" />

      {/* Bottom Section */}
      <div className="flex justify-between items-center">
        {/* Right Arrow to Navigate */}
        <button
          className="flex items-center gap-1 text-gray-400 hover:text-yellow-500 transition"
          onClick={handleNavigate}
        >
          <span className="text-sm">
            {quote?.items?.length} {t("product.items")}
          </span>
        </button>
      </div>
    </div>
  )
}

export default function AccountQuoteListsClient({ accountContext, allItems }: AccountQuotesClientProps) {
  const [currentPage, setCurrentPage] = useState(1)
  const [searchText, setSearchText] = useState("")
  const [searchQuery, setSearchQuery] = useState("")
  const itemsPerPage = 5

  // Filter items based on search query
  const filteredItems = allItems.filter((item) => item.displayName?.toLowerCase().includes(searchQuery.toLowerCase()))

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage)

  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentItems = filteredItems.slice(startIndex, endIndex)

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery])

  const t = useTranslations("storefront")

  return (
    <>
      <div className="flex flex-col items-center justify-center p-8 border rounded-md">
        {/* Bear paw icon */}
        <div className="mb-4">
          <Image src={"/img/bear-paw.png"} alt={"bear-paw"} width={145} height={122}></Image>
        </div>

        {/* Heading and Subtext */}
        <h2 className="text-xl font-bold text-gray-800">{t("quote.my_quotes")}</h2>
        <p className="text-sm text-gray-600 mb-8">{t("quote.created_quotes", { count: allItems.length })}</p>

        {/* Steps */}
        <div className="flex flex-col sm:flex-row items-center gap-4">
          {/* Step 1 */}
          <div className="flex items-center border rounded-lg px-6 py-4 bg-white w-[15rem] h-[5rem]">
            <div className="flex items-center justify-center w-6 h-6 text-yellow-500 text-6xl mr-3">1</div>
            <div className="text-sm text-gray-700">{t("quote.step1")}</div>
          </div>

          <div className="flex w-[30px]">
            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
              <line x1="0" y1="0" x2="100" y2="50" stroke="#f5f5f5" strokeWidth="10"></line>
              <line x1="0" y1="100" x2="100" y2="50" stroke="#f5f5f5" strokeWidth="10"></line>
            </svg>
          </div>

          {/* Step 2 */}
          <div className="flex items-center border rounded-lg px-6 py-4 bg-white w-[15rem] h-[5rem]">
            <div className="flex items-center justify-center w-6 h-6 text-yellow-500 mr-3 text-6xl">2</div>
            <div className="text-sm text-gray-700">{t("quote.step2")}</div>
          </div>

          <div className="flex w-[30px]">
            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
              <line x1="0" y1="0" x2="100" y2="50" stroke="#f5f5f5" strokeWidth="10"></line>
              <line x1="0" y1="100" x2="100" y2="50" stroke="#f5f5f5" strokeWidth="10"></line>
            </svg>
          </div>

          {/* Step 3 */}
          <div className="flex items-center border rounded-lg px-6 py-4 bg-white w-[15rem] h-[5rem]">
            <div className="flex items-center justify-center w-6 h-6 text-yellow-500 text-6xl mr-3">3</div>
            <div className="text-sm text-gray-700">{t("quote.step3")}</div>
          </div>
        </div>
      </div>

      {/* Search box and Quotes list container */}
      <div className="flex flex-col gap-4">
        {/* Search box in a flex container */}
        <div className="flex justify-end mb-4">
          <div className="relative w-full sm:w-64">
            <SearchIcon
              strokeWidth={2}
              className="pointer-events-none absolute right-1 top-1/2 size-5 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder={t("quote.search_placeholder")}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setSearchQuery(searchText)
                }
              }}
              className="w-[103%] rounded-md border border-gray-300 py-2 pl-4 pr-10 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Quotes list */}
        {currentItems.map((item) => (
          <QuoteCard
            key={item.id}
            quote={item}
            isoLocale={accountContext.isoLocale}
            isQuoteRequest={item.isQuoteRequest}
          />
        ))}
      </div>

      <DataPagination initialPage={currentPage} totalPages={totalPages} onPageChange={(page) => setCurrentPage(page)} />
    </>
  )
}
